// import global variables.
import {BODY_ELEMENT, FOCUS_ELEMENTS, tabindexControl} from '../utils/global';
import * as backdrop from '../utils/backdrop';
import scrollLock from '../utils/scrollLock';

export class Modal {
    /**
     * モーダル
     *
     * @constructor
     * @param {HTMLElement} root - ルートとなる要素
     * @param {Object} options - 設定の変更をする際のオブジェクト
     */
    constructor(root, options) {
        const config = {
            className: {
                label: 'js-modal__label',
                description: 'js-modal__description',
                existsCloseButton: 'js-modal__close',
                closeButton: 'p-modal__close',
                backButton: 'p-modal__back',
                altText: 'u-altText'
            },
            prefix: {
                id: 'modal-'
            }
        };

        // ルートとなる要素が無い場合は実装しない
        if (!root) {
            return;
        }

        this.config = Object.assign(config, options);
        this.root = root;
        this.id = this.root.dataset.hasOwnProperty('href') ? this.root.dataset.href.slice(1) : this.root.getAttribute('href').slice(1);
        this.content = document.getElementById(this.id);
        this.modalLabel = this.content.querySelector(`.${this.config.className.label}`);
        this.modalDescription = this.content.querySelector(`.${this.config.className.description}`);
        this.youtubeIframeElement = this.content.querySelector('iframe[src^="https://www.youtube.com/embed/"]');
        this.closeButton = document.createElement('button');
        this.existsCloseButton = this.content.querySelectorAll(`.${this.config.className.existsCloseButton}`);
        this.backButton = document.createElement('button');
        this.backdrop = backdrop.createBackdropLayer();
        this.focusableElements = [...this.content.querySelectorAll(FOCUS_ELEMENTS), this.content];
        this.alreadyOpenedModaId = '';
        this.isOpen = false;
        this.isDisablePopState = false;

        // コンテンツが見つからない場合は実装しない
        if (!this.content) {
            return;
        }

        this.init();
        this.addEvent();
    }

    /**
     * init - 初期化
     * @return {Void}
     */
    init() {
        // button要素以外にはbutton roleを追加
        if (this.root.tagName.toLowerCase() !== 'button') {
            this.root.setAttribute('role', 'button');
            this.root.tabIndex = 0;
        }

        // モーダルのラベルが指定されている場合の紐づけ
        if (this.modalLabel) {
            this.modalLabel.id = `${this.id}-label`;
            this.content.setAttribute('aria-labelledby', `${this.id}-label`);
        }

        // モーダルの説明が指定されている場合の紐づけ
        if (this.modalDescription) {
            this.modalDescription.id = `${this.id}-description`;
            this.content.setAttribute('aria-describedby', `${this.id}-description`);
        }

        this.closeButton.type = 'button';
        this.closeButton.classList.add(this.config.className.closeButton);
        this.closeButton.insertAdjacentHTML('beforeend', `<span class="${this.config.className.altText}">閉じる</span>`);

        this.backButton.type = 'button';
        this.backButton.classList.add(this.config.className.backButton);
        this.backButton.insertAdjacentHTML('beforeend', `<span class="${this.config.className.altText}">戻る</span>`);

        this.root.setAttribute('aria-expanded', 'false');
        this.root.setAttribute('aria-haspopup', 'true');
        this.root.setAttribute('aria-controls', this.id);

        this.content.hidden = true;
        this.content.tabIndex = -1;
        this.content.setAttribute('role', 'dialog');
        this.content.setAttribute('aria-modal', 'true');

        this.content.insertAdjacentElement('beforeend', this.closeButton);
        this.focusableElements.push(this.closeButton);
    }

    /**
     * addEvent - イベントバインド
     */
    addEvent() {
        this.root.addEventListener('click', (e) => {
            const enableModalId = BODY_ELEMENT.dataset.modal;

            // 既に開いているモーダルが無い場合は、開く処理のみ実行
            if (!enableModalId) {
                this.modalOpen(e);

                return;
            }

            const swapTarget = document.getElementById(enableModalId);
            const event = document.createEvent('Event');

            // IE11対策 かつ `initEvent`は廃止予定のため回避
            if (typeof event.initEvent === 'function') {
                event.initEvent('swap', false, true);
            }

            // モーダルの入れ替え表示を実行
            swapTarget.dispatchEvent(event);
            this.modalSwap(e, enableModalId);
        });

        this.content.addEventListener('swap', (e) => {
            this.modalSwap(e, BODY_ELEMENT.dataset.modal);
        });

        this.content.addEventListener(backdrop.eventName, (e) => {
            this.beforeClose();
            this.modalClose(e);
        });

        this.content.addEventListener('keydown', (e) => {
            // 閉じている場合は無効
            if (!this.isOpen || e.key.indexOf('Esc') === -1) {
                return;
            }

            // Escapeキー押下で閉じる
            this.beforeClose();
            this.modalClose(e);
        });

        for (const button of [this.closeButton, ...this.existsCloseButton]) {
            button.addEventListener('click', (e) => {
                this.beforeClose();
                this.modalClose(e);
            });
        }

        this.backButton.addEventListener('click', () => {
            BODY_ELEMENT.dataset.relatedModalCount = (parseInt(BODY_ELEMENT.dataset.relatedModalCount, 10) || 0) + 1;
            window.history.back();
        });

        window.addEventListener('popstate', (e) => {
            // popstate無効化フラグがtrueの場合は何もしない
            if (this.isDisablePopState) {
                return;
            }

            // すでに開いているモーダルが無い場合（現在開いているモーダルが一番初めに開かれたものである）
            if (this.alreadyOpenedModaId === '') {
                // 開いている場合、閉じるし処理を実行
                if (this.isOpen) {
                    BODY_ELEMENT.removeAttribute('data-related-modal-count');
                    this.modalClose(e);
                }

                return;
            }

            // モーダルが閉じている場合 または popstateEvent.state とid名が一致しない場合は何もしない
            if (!this.isOpen || this.alreadyOpenedModaId !== e.state.alreadyOpenedModaId) {
                return;
            }

            const swapTarget = document.getElementById(this.alreadyOpenedModaId);
            const event = document.createEvent('Event');

            this.alreadyOpenedModaId = '';
            BODY_ELEMENT.dataset.relatedModalCount = (parseInt(BODY_ELEMENT.dataset.relatedModalCount, 10) || 0) - 1;

            // IE11対策 かつ `initEvent`は廃止予定のため回避
            if (typeof event.initEvent === 'function') {
                event.initEvent('swap', false, true);
            }

            // 閉じる処理を実行
            this.content.removeChild(this.backButton);
            this.focusableElements.pop();
            this.modalClose(e);
            swapTarget.dispatchEvent(event);
        });
    }

    /**
     * beforeClose - 閉じる前の処理
     * @return {void}
     */
    beforeClose() {
        // popstateを無効化
        this.isDisablePopState = true;

        // モーダルが開かれた分、履歴を戻す
        window.history.go((parseInt(BODY_ELEMENT.dataset.relatedModalCount, 10) || 0) * -1);
        BODY_ELEMENT.removeAttribute('data-related-modal-count');
    }

    /**
     * modalOpen - 開く処理
     * @param  {clickEvent|swapEvent} event イベントオブジェクト
     * @return {void}
     */
    modalOpen(event) {
        // すでに展開されている場合は何もしない
        if (this.isOpen) {
            return;
        }

        // モーダルが開かれている数を属性から取得
        const relatedModalCount = parseInt(BODY_ELEMENT.dataset.relatedModalCount, 10) || 0;

        event.preventDefault();

        // フラグを更新
        this.isOpen = true;
        this.content.hidden = false;

        BODY_ELEMENT.dataset.modal = this.id;
        this.root.setAttribute('aria-expanded', 'true');
        this.content.focus();

        // swapイベントではない場合、履歴を追加
        if (event.type !== 'swap') {
            this.isDisablePopState = false;
            BODY_ELEMENT.dataset.relatedModalCount = relatedModalCount + 1;
            window.history.pushState({alreadyOpenedModaId: this.id}, null);

        // swapイベントの場合は属性の更新のみ
        } else {
            BODY_ELEMENT.dataset.relatedModalCount = relatedModalCount - 1;
        }

        // YouTube埋め込み動画が存在する場合、ミュートにし再生をスタート
        if (this.youtubeIframeElement !== null) {
            this.controllYoutubeMovie('mute');
            this.controllYoutubeMovie('playVideo');
        }

        // タブインデックス・スクロールの制御
        tabindexControl(true, this.focusableElements);
        backdrop.displayBackdropLayer(true, this.id);
        scrollLock(true);
    }

    /**
     * modalClose - 閉じる処理
     * @param  {popstateEvent|clickEvent|KeydownEvent|swapEvent} event イベントオブジェクト
     * @return {Void}
     */
    modalClose(event) {
        // すでに閉じられている場合は何もしない
        if (!this.isOpen) {
            return;
        }

        // フラグを更新
        this.isOpen = false;
        this.content.hidden = true;

        BODY_ELEMENT.removeAttribute('data-modal');
        this.root.setAttribute('aria-expanded', 'false');

        // modal in modalの時は一番初めモーダル起動ボタンにフォーカスさせる
        if ((event.type !== 'swap' && event.type !== 'popstate') && BODY_ELEMENT.dataset.modalRootId) {
            document.querySelector(`[aria-controls="${BODY_ELEMENT.dataset.modalRootId}"]`).focus();
            BODY_ELEMENT.removeAttribute('data-modal-root-id');

        // modal in modalではない場合はコンテンツにフォーカス
        } else {
            this.root.focus();
        }

        // YouTube埋め込み動画が存在する場合、再生を停止（一時停止）する
        if (this.youtubeIframeElement !== null) {
            this.controllYoutubeMovie('pauseVideo');
        }

        // タブインデックス・スクロールの制御
        tabindexControl(false, this.focusableElements);
        backdrop.displayBackdropLayer(false);
        scrollLock(false);
    }

    /**
     * modalSwap - 入れ替え処理
     * @param  {clickEvent|swapEvent} event         イベントオブジェクト
     * @param  {String} enableModalId 現在開かれているモーダルのid名
     * @return {Void}
     */
    modalSwap(event, enableModalId) {
        // モーダルが開いている場合は、履歴を残さず閉じる処理
        if (this.isOpen) {
            this.modalClose(event);

            return;
        }

        // 閉じている場合は履歴を残し、開く
        if (event.type !== 'swap') {
            this.alreadyOpenedModaId = enableModalId;
            this.content.appendChild(this.backButton);
            this.focusableElements.push(this.backButton);

            // data-modal-root-id属性がない場合、IDを登録
            if (!BODY_ELEMENT.dataset.modalRootId) {
                BODY_ELEMENT.dataset.modalRootId = enableModalId;
            }
        }

        // 開く処理を実行
        this.modalOpen(event);
    }

    /**
     * controllYoutubeMovie - YouTube埋め込み動画の制御
     * @param  {String} functionCommand 実行するコマンド名（mute|playVideo|pauseVideo|stopVideo|clearVideo）
     * @return {Void}
     */
    controllYoutubeMovie(functionCommand) {
        const command = {
            event: 'command',
            func: functionCommand
        };

        // コマンドを実行
        this.youtubeIframeElement.contentWindow.postMessage(JSON.stringify(command), '*');
    }
}

/**
 * デフォルト実行処理
 * @return {Void}
 */
export default () => {
    // 汎用モーダル
    document.querySelectorAll('.js-modal').forEach((element) => new Modal(element));
};
